/* pantallas-alumno (F): LAS PIEZAS DE ANALITICAS (parte 2, "Piezas nuevas" y seccion 2).

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   - Readiness con la regla del Panel (aprobado por Carlos): verde con 85 o mas, naranja abajo, la MISMA
     regla que la tarjeta del Panel (`colorDePreparacion` de `lib/preparacionDelPanel.js`). La palabra de
     la zona siempre se escribe ("{Zone}, {pct}%"). Chip verde o chip naranja suave del Panel; barra
     #4ADE80 o #F27A2E; textos #4ADE80 o #F7A06A.
   - El globo de Readiness es el panel flotante del Panel (`CAJA_FLOTANTE`, uno a la vez): con el mouse
     sale al pasar por el chip; con el dedo, al tocarlo (P5), sin abrir el detalle. En el detalle, el
     boton (i) de la tarjeta de Readiness abre el mismo texto.
   - Tarjeta de Analiticas: la anatomia fija de la tarjeta del Panel (encabezado de 40, detalle de 36 mas
     su aire, pie de 44). Sin Readiness el pie queda vacio en computadora (la fila mide lo mismo) y se
     quita abajo de 1024, donde va una por fila. Toda la tarjeta abre el detalle.
   - P4: "{{studied}}/{{total}} topic studied" con total 1: se pasa `count` y el plural de hoy es la
     llave sin sufijo.
   Los textos son los de hoy, con su llave de hoy. */
import React, { useCallback, useRef, useState } from 'react';
import { ChevronRight, Info, Play, Users } from 'lucide-react';
import {
  TARJETA, ZONA_ENCABEZADO, ZONA_DETALLE, ZONA_PIE, CAJA_ICONO, TRAZO_ICONO, TITULO, RENGLON, PISTA,
  AreaDe44, nombreConCortes,
} from '../panel/anatomia';
import Flotante, { CAJA_FLOTANTE } from '../panel/Flotante';
import { useCierreDeFlotante } from '../panel/unoALaVez';
import ChipDeEstado from '../panel/ChipDeEstado';
import { colorDePreparacion } from '../../lib/preparacionDelPanel';
import { textoDelUltimoRepaso } from '../../lib/diasDeCalendario';
import { GlifoDeMaterial } from '../IconoDeMaterial';
import { tipoDeMaterial } from '../../lib/tipoDeMaterial';
import {
  Boton, ChipVerde, COLORES_DEL_PUNTAJE, FilaDeResumen, NumeroDeResumen, PuntoSeparador, BloqueEsqueleto,
} from '../alumno';

const VERDE = '#4ADE80';
const NARANJA_TEXTO = '#F7A06A';

/** La regla del Panel: verde con 85 o mas. */
export function readinessVerde(score) {
  return colorDePreparacion(Number(score)) === VERDE;
}
export const colorDeBarra = (score) => colorDePreparacion(Number(score));
export const colorDeTexto = (score) => (readinessVerde(score) ? VERDE : NARANJA_TEXTO);

/** "{Zone}, {pct}%" con la llave de hoy. */
export function textoDelChip(t, readiness) {
  return `${t(`study:readiness.zones.${readiness.zone_key}.label`)}, ${readiness.score}%`;
}

/** P4: el conteo de temas estudiados, con `count` (la forma _one con total 1). */
export function textoDeTemasEstudiados(t, readiness) {
  return t('study:readiness.topics_studied', {
    studied: readiness.studied_topics, total: readiness.total_topics, count: readiness.total_topics,
  });
}

export function ChipDeReadiness({ t, readiness, testid }) {
  const texto = textoDelChip(t, readiness);
  return readinessVerde(readiness.score)
    ? <ChipVerde testid={testid}>{texto}</ChipVerde>
    : <ChipDeEstado tono="naranja" testid={testid}>{texto}</ChipDeEstado>;
}

/* El chip que abre el globo: con el mouse al pasar (y se va al salir, si no se toco); con el dedo o el
   clic, al tocar, sin dejar que el toque llegue a la tarjeta. La caja se ancla al primer ancestro
   posicionado de la caja (la tarjeta), no al boton: sale a lo ancho, debajo, encima de lo de abajo. */
export function ChipConGlobo({ t, readiness, testid, testidBoton, testidGlobo }) {
  const [abierto, setAbierto] = useState(false);
  const porMouse = useRef(false);
  const cajaRef = useRef(null);
  const botonRef = useRef(null);
  const cerrar = useCallback(() => { porMouse.current = false; setAbierto(false); }, []);
  useCierreDeFlotante(abierto, cerrar, cajaRef, botonRef);
  const alTocar = (e) => {
    e.stopPropagation();
    if (abierto && !porMouse.current) { cerrar(); return; }
    porMouse.current = false;
    setAbierto(true);
  };
  const alEntrar = (e) => {
    if (e.pointerType !== 'mouse' || abierto) return;
    porMouse.current = true;
    setAbierto(true);
  };
  const alSalir = (e) => {
    if (e.pointerType !== 'mouse' || !porMouse.current) return;
    cerrar();
  };
  return (
    <>
      <button type="button" ref={botonRef} onClick={alTocar} onPointerEnter={alEntrar} onPointerLeave={alSalir}
        onKeyDown={(e) => e.stopPropagation()} aria-expanded={abierto} aria-label={t('study:readiness.tooltip')}
        data-testid={testidBoton}
        className="relative inline-flex shrink-0 min-w-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white cursor-help">
        <ChipDeReadiness t={t} readiness={readiness} testid={testid} />
        <AreaDe44 />
      </button>
      {abierto ? (
        <div ref={cajaRef} role="tooltip" className={`${CAJA_FLOTANTE} text-[13px] leading-[18px] text-[#C8C8C8]`}
          onClick={(e) => e.stopPropagation()} data-testid={testidGlobo}>
          {t('study:readiness.tooltip')}
        </div>
      ) : null}
    </>
  );
}

/* La linea del detalle de la tarjeta: "{n} topics, Best exam: {pct}%, Last review: {when}", en ese orden. */
export function lineaDeLaTarjeta(t, card, ahora = new Date()) {
  const piezas = [t('analytics.card.topics', { count: card.topics_count })];
  if (!card.has_adaptive_data && card.has_exam_data && card.best_exam_score != null) {
    piezas.push(t('analytics.card.best_exam', { pct: card.best_exam_score }));
  }
  const cuando = card.last_studied ? textoDelUltimoRepaso(t, card.last_studied, ahora) : null;
  if (cuando) piezas.push(`${t('analytics.topic.last_review')}: ${cuando}`);
  return piezas.join(', ');
}

/* La tarjeta de la vista general. Clases de la caja escritas enteras (Tailwind las lee del archivo). */
const CAJA_DE_LA_TARJETA = `${TARJETA} w-full text-left cursor-pointer select-none transition-colors hoverable:border-[#3A2414] active:bg-[#1A1A1A] active:border-[#3A2414] motion-safe:active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-white`;

export function TarjetaDeAnaliticas({ card, i, t, alAbrir }) {
  const conReadiness = !!(card.readiness && card.readiness.has_data);
  const esCard = !!card.publicacion_id;
  const nombre = card.filename;
  const alTecla = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); alAbrir(); }
  };
  return (
    <div role="button" tabIndex={0} onClick={alAbrir} onKeyDown={alTecla} data-testid={`doc-card-${i}`}
      data-readiness={conReadiness ? 'si' : 'no'} className={CAJA_DE_LA_TARJETA}>
      <div className={ZONA_ENCABEZADO} data-testid={`doc-card-${i}-encabezado`}>
        <div className={CAJA_ICONO} aria-hidden="true">
          {esCard
            ? <Users className={TRAZO_ICONO} />
            : <GlifoDeMaterial tipo={tipoDeMaterial(card)} className={TRAZO_ICONO} />}
        </div>
        <p className={TITULO} title={nombre} data-testid={`doc-card-title-${i}`}>{nombreConCortes(nombre)}</p>
        <ChevronRight aria-hidden="true" data-testid={`doc-card-chevron-${i}`}
          className="w-5 h-5 mt-2.5 shrink-0 text-[#7E7E7E] transition-colors group-hover/tarjeta:text-[#F7A06A] group-active/tarjeta:text-[#F7A06A] group-active/tarjeta:duration-0" />
      </div>
      <div className={ZONA_DETALLE} data-testid={`doc-card-${i}-detalle`}>
        {esCard ? (
          <p className={`${RENGLON} flex items-center gap-1.5`} data-testid={`doc-card-grupo-${i}`}>
            <Users aria-hidden="true" className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate min-w-0">{card.grupo_nombre}</span>
          </p>
        ) : null}
        <p className={RENGLON} data-testid={`doc-card-meta-${i}`}>{lineaDeLaTarjeta(t, card)}</p>
      </div>
      <div className={conReadiness ? ZONA_PIE : `${ZONA_PIE} max-lg:hidden`} data-testid={`doc-card-${i}-pie`}>
        {conReadiness ? (
          <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5">
            <div className="h-[22px] flex items-center justify-between gap-2 min-w-0">
              <ChipConGlobo t={t} readiness={card.readiness} testid={`doc-card-readiness-${i}`}
                testidBoton={`doc-card-readiness-btn-${i}`} testidGlobo={`doc-card-readiness-tip-${i}`} />
              <span className="text-[12px] leading-4 text-[#9A9A9A] truncate min-w-0" data-testid={`doc-card-temas-${i}`}>
                {textoDeTemasEstudiados(t, card.readiness)}
              </span>
            </div>
            <div className={PISTA}>
              <div className="h-full rounded-full" data-testid={`doc-card-barra-${i}`}
                style={{ width: `${Math.max(0, Math.min(100, Number(card.readiness.score) || 0))}%`, backgroundColor: colorDeBarra(card.readiness.score) }} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* La tarjeta de Readiness del detalle: "Readiness Score" con su (i), el numero en el color de la regla,
   la barra de 10, la zona en negritas y el conteo de temas, y "Start Studying" a lo ancho. */
export function TarjetaDeReadiness({ t, readiness, alEstudiar }) {
  const color = colorDeTexto(readiness.score);
  return (
    <div className={`${TARJETA} md:p-5 gap-3`} data-testid="readiness-bar-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-[15px] font-semibold text-[#C8C8C8] truncate">{t('study:readiness.header')}</span>
          <Flotante
            testid="readiness-info-tip"
            boton={(props) => (
              <button type="button" {...props} aria-label={t('study:readiness.header')} data-testid="readiness-info-btn"
                className="w-11 h-11 md:w-8 md:h-8 shrink-0 rounded-lg flex items-center justify-center text-[#9A9A9A] hoverable:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white">
                <Info className="w-4 h-4" aria-hidden="true" />
              </button>
            )}
            contenido={() => <p className="text-[13px] leading-[18px] text-[#C8C8C8]">{t('study:readiness.tooltip')}</p>}
          />
        </div>
        <span className="text-[34px] md:text-[40px] leading-none font-bold tabular-nums" style={{ color }} data-testid="readiness-score">{readiness.score}%</span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-[#262626] overflow-hidden" data-testid="readiness-bar">
        <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, Number(readiness.score) || 0))}%`, backgroundColor: colorDeBarra(readiness.score) }} />
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[14px] font-bold" style={{ color }} data-testid="readiness-zone-label">{t(`study:readiness.zones.${readiness.zone_key}.label`)}</span>
        <span className="text-[13px] text-[#9A9A9A]">{textoDeTemasEstudiados(t, readiness)}</span>
      </div>
      <Boton alto="grande" aLoAncho icono={Play} onClick={alEstudiar} testid="readiness-study-btn">{t('analytics.start_studying')}</Boton>
    </div>
  );
}

/* "Topic mastery: {n}%" con el numero en blanco y negritas, sin cambiar el texto de hoy. */
const MARCA = '\u0001';
function DominioDelTema({ t, mastery }) {
  const texto = t('analytics.topic.mastery', { mastery: MARCA, interpolation: { escapeValue: false } });
  const partes = String(texto).split(MARCA);
  if (partes.length !== 2) return <span>{t('analytics.topic.mastery', { mastery })}</span>;
  return (
    <span>{partes[0]}<strong className="font-bold text-[#F4F4F3]">{mastery}</strong>{partes[1]}</span>
  );
}

/* La tarjeta de tema (los estados especiales de hoy, ahora como chips grises):
     total_attempts === 0                          -> "Not started" (nada mas)
     total_attempts > 0 && unique_attempted === 0  -> la linea de practica + "Not enough data yet" (sin barra) */
export function TarjetaDeTema({ topic, i, t }) {
  const attempts = topic.total_attempts || 0;
  const unique = topic.unique_attempted || 0;
  const total = topic.total_questions_mastery;
  const mastery = topic.mastery || 0;
  const cuando = topic.last_attempted ? textoDelUltimoRepaso(t, topic.last_attempted, new Date()) : null;
  return (
    <div className="rounded-[16px] border border-[#222222] bg-[#141414] p-4 flex flex-col gap-2" data-testid={`topic-stat-${i}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-[15px] leading-5 font-semibold text-[#F4F4F3] line-clamp-2 break-words" title={topic.topic}>{topic.topic}</p>
        {attempts === 0 ? (
          <ChipDeEstado tono="gris" testid={`topic-not-started-${i}`} className="shrink-0">{t('analytics.topic.not_started')}</ChipDeEstado>
        ) : unique === 0 ? (
          <ChipDeEstado tono="gris" testid={`topic-not-enough-${i}`} className="shrink-0">{t('analytics.topic.not_enough_data')}</ChipDeEstado>
        ) : null}
      </div>
      {attempts > 0 ? (
        <p className="text-[13px] leading-[18px] text-[#9A9A9A]" data-testid={`topic-raw-${i}`}>
          {t('analytics.topic.raw', {
            correct: t('analytics.topic.correct', { count: topic.correct_attempts || 0 }),
            attempts: t('analytics.topic.attempts', { count: attempts }),
          })}
          {cuando ? <>. {t('analytics.topic.last_review')}: <span>{cuando}</span></> : null}
        </p>
      ) : null}
      {attempts > 0 && unique > 0 ? (
        <div className="flex flex-col gap-1.5" data-testid="mastery-bar">
          <p className="text-[13px] leading-[18px] text-[#9A9A9A]"><DominioDelTema t={t} mastery={mastery} /></p>
          <div className={PISTA}>
            <div className="h-full rounded-full bg-[#F27A2E]" style={{ width: `${Math.max(0, Math.min(100, mastery))}%` }} />
          </div>
          {total > 0 ? (
            <p className="text-[12.5px] leading-[18px] text-[#9A9A9A]" data-testid={`topic-coverage-${i}`}>
              {unique >= total
                ? t('analytics.topic.coverage_full', { count: total })
                : t('analytics.topic.coverage_partial', { unique, count: total })}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Los cortes de hoy de Topic Accuracy: 70 o mas verde, 50 a 69 naranja (antes ambar), abajo rojo. */
export function colorDePrecision(accuracy) {
  if (accuracy >= 70) return COLORES_DEL_PUNTAJE.alto;
  if (accuracy >= 50) return COLORES_DEL_PUNTAJE.medio;
  return COLORES_DEL_PUNTAJE.bajo;
}

/* Exam Results: los tres numeros, Topic Accuracy y Past Exams (con el punto dibujado y el chip "Best").
   Con mas de 5 examenes la lista se desplaza dentro de su caja, como hoy, con barrita y degradado. */
export function ResultadosDeExamen({ exam, t, idioma }) {
  const conCaja = exam.sessions.length > 5;
  return (
    <div className="flex flex-col gap-5" data-testid="exam-results-content">
      <FilaDeResumen testid="exam-score-summary">
        <NumeroDeResumen etiqueta={t('analytics.exam.best_score')} colorDelNumero={VERDE}
          numero={<span data-testid="exam-best-score">{exam.best_score}%</span>} />
        <NumeroDeResumen etiqueta={t('analytics.exam.average')}
          numero={<span data-testid="exam-avg-score">{exam.average_score}%</span>} />
        <NumeroDeResumen etiqueta={t('analytics.exam.exams_taken')}
          numero={<span data-testid="exam-total-count">{exam.total_exams}</span>} />
      </FilaDeResumen>

      {exam.topic_accuracy.length > 0 ? (
        <div className="rounded-[18px] border border-[#222222] bg-[#141414] px-4 pt-4 pb-1 md:px-5" data-testid="exam-topic-accuracy">
          <h3 className="text-[16px] font-semibold text-[#F4F4F3] mb-1">{t('analytics.exam.topic_accuracy')}</h3>
          <div>
            {exam.topic_accuracy.map((fila, i) => (
              <div key={i} className="h-11 flex items-center justify-between gap-3 border-b border-[#1F1F1F] last:border-0" data-testid={`exam-topic-${i}`}>
                <span className="min-w-0 truncate text-[14px] text-[#C8C8C8]" title={fila.topic}>{fila.topic}</span>
                <span className="flex items-center gap-3 shrink-0">
                  <span className="text-[13px] tabular-nums text-[#7E7E7E]">{fila.correct}/{fila.total}</span>
                  <span className="text-[14px] font-bold tabular-nums" style={{ color: colorDePrecision(fila.accuracy) }}>{fila.accuracy}%</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div data-testid="exam-sessions-list">
        <h3 className="text-[19px] font-semibold text-[#F4F4F3] mb-3">{t('analytics.exam.past_exams')}</h3>
        <div className="relative">
          <div className={conCaja ? 'flex flex-col gap-2 max-h-[60dvh] md:max-h-[440px] overflow-y-auto pr-1 pb-12 dashboard-doc-scroll' : 'flex flex-col gap-2'}>
            {exam.sessions.map((s, i) => (
              <div key={i} className="min-h-[72px] rounded-[14px] border border-[#222222] bg-[#141414] px-4 py-3 flex items-center justify-between gap-3" data-testid={`exam-session-${i}`}>
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-[#F4F4F3]">{t('analytics.exam.session_result', { correct: s.correct, total: s.total, score: s.score })}</p>
                  <div className="mt-1 flex items-center flex-wrap text-[13px] text-[#9A9A9A]">
                    <span>{new Date(s.date).toLocaleDateString(idioma, { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    {s.time_taken_minutes != null ? <><PuntoSeparador /><span className="sr-only">, </span><span>{t('analytics.exam.minutes', { min: s.time_taken_minutes })}</span></> : null}
                    {s.time_limit ? <><PuntoSeparador /><span className="sr-only">, </span><span>{t('analytics.exam.limit', { min: s.time_limit })}</span></> : null}
                  </div>
                </div>
                {s.score === exam.best_score ? <ChipVerde testid="best-score-badge">{t('analytics.exam.best')}</ChipVerde> : null}
              </div>
            ))}
          </div>
          {conCaja ? <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-12 rounded-b-[14px] bg-gradient-to-t from-[#0E0E0E] to-transparent" /> : null}
        </div>
      </div>
    </div>
  );
}

/* AN9: el esqueleto de la vista general copia el acomodo nuevo: tres numeros y tarjetas con icono,
   nombre, detalle y pie (con las mismas zonas de alto fijo). El encabezado lo pinta la pagina. */
export function EsqueletoDeAnaliticas({ tarjetas = 4 }) {
  return (
    <div className="flex flex-col gap-5 md:gap-6" data-testid="skeleton-analytics" aria-hidden="true">
      <div className="grid grid-cols-3 gap-2 md:gap-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-[18px] border border-[#222222] bg-[#141414] p-3 md:py-4 md:px-[18px]">
            <BloqueEsqueleto className="h-3 w-16 max-w-full" />
            <BloqueEsqueleto className="h-6 md:h-8 w-10 mt-2" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {Array.from({ length: tarjetas }, (_, i) => (
          <div key={i} className={TARJETA}>
            <div className={ZONA_ENCABEZADO}>
              <BloqueEsqueleto className="w-10 h-10 rounded-xl shrink-0" />
              <BloqueEsqueleto className="h-4 w-3/5 mt-1" />
            </div>
            <div className={ZONA_DETALLE}><BloqueEsqueleto className="h-3 w-2/5" /></div>
            <div className={ZONA_PIE}>
              <div className="flex-1 flex flex-col gap-2">
                <BloqueEsqueleto className="h-[18px] w-24" />
                <BloqueEsqueleto className="h-1.5 w-full" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* AN13: el esqueleto del detalle: regreso, titulo, pestanas, tarjeta de Readiness y temas. */
export function EsqueletoDelDetalle() {
  return (
    <div className="flex flex-col gap-5 md:gap-6" data-testid="skeleton-analytics-doc-detail" aria-hidden="true">
      <BloqueEsqueleto className="h-11 w-40 rounded-full" />
      <div className="flex items-start gap-3">
        <BloqueEsqueleto className="w-10 h-10 rounded-xl shrink-0" />
        <div className="flex-1 flex flex-col gap-2">
          <BloqueEsqueleto className="h-7 w-3/5" />
          <BloqueEsqueleto className="h-4 w-2/5" />
        </div>
      </div>
      <BloqueEsqueleto className="h-11 md:h-12 w-full rounded-xl" />
      <div className={`${TARJETA} md:p-5 gap-3`}>
        <BloqueEsqueleto className="h-9 w-full" />
        <BloqueEsqueleto className="h-2.5 w-full" />
        <BloqueEsqueleto className="h-12 w-full rounded-xl" />
      </div>
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-[16px] border border-[#222222] bg-[#141414] p-4 flex flex-col gap-2">
          <BloqueEsqueleto className="h-4 w-2/5" />
          <BloqueEsqueleto className="h-3 w-3/5" />
          <BloqueEsqueleto className="h-1.5 w-full" />
        </div>
      ))}
    </div>
  );
}

